import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { MISSED_CUE_GRACE_MS, cueDone, unlockAudio } from './audio';
import { useWakeLock } from './useWakeLock';

// ActiveRest is the state of a single in-flight rest countdown. The label
// describes which set/exercise the user is resting from; expiresAt is the
// canonical moment the timer fires (server-side) — the UI computes
// `seconds remaining = expiresAt - now`. id is the scheduled_notifications
// row id, used to cancel the pending push when the user starts the next set.
export type ActiveRest = {
  id: number;
  label: string;
  expiresAtMs: number;
  totalSec: number;
  phase: 'running' | 'expired';
};

export function useRestTimer() {
  const [active, setActive] = useState<ActiveRest | null>(null);
  // Tick state — drives a 250ms interval so the displayed countdown
  // smoothly updates without re-rendering on every animation frame.
  const [, setTick] = useState(0);
  const expireTimerRef = useRef<number | null>(null);
  const tickIntervalRef = useRef<number | null>(null);
  const startBusyRef = useRef(false);
  // expiresAtMs of the timer we've already sounded, so the setTimeout and the
  // on-wake resolver below can't both cue the same rest.
  const cuedRef = useRef(0);

  // Keep the screen awake while resting. Without it iOS dims, locks, and
  // suspends our timers a few seconds later — which is precisely the bug where
  // the countdown "stops" mid-set.
  useWakeLock(active?.phase === 'running');

  // expire is the single path from running → expired, reached either by the
  // setTimeout (page alive) or by the visibility resolver (page was suspended
  // and the deadline passed while we were gone). Idempotent, so both may fire.
  const expire = useCallback((expiresAtMs: number) => {
    setActive((prev) =>
      prev && prev.expiresAtMs === expiresAtMs && prev.phase === 'running'
        ? { ...prev, phase: 'expired' }
        : prev,
    );
    if (cuedRef.current === expiresAtMs) return;
    cuedRef.current = expiresAtMs;
    if (Date.now() - expiresAtMs <= MISSED_CUE_GRACE_MS) cueDone();
  }, []);

  // Coming back from a locked screen: setTimeout may have been suspended past
  // its deadline, so settle it against the wall clock the moment we're visible
  // rather than trusting a timer the OS was free to stall.
  useEffect(() => {
    if (!active || active.phase !== 'running') return;
    const { expiresAtMs } = active;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() >= expiresAtMs) expire(expiresAtMs);
      else setTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [active, expire]);

  // clearTimers tears down both the expiry callback and the tick interval.
  // Used on unmount, on cancel, and before starting a fresh rest.
  const clearTimers = useCallback(() => {
    if (expireTimerRef.current !== null) {
      window.clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
    if (tickIntervalRef.current !== null) {
      window.clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }, []);

  // start kicks off a new rest timer. The local UI countdown updates
  // synchronously and unconditionally — it does not wait on the server
  // schedule, and a busy guard never swallows it. We then best-effort
  // schedule a backup push for the case where the user backgrounds the
  // tab. If a previous timer is replaced before its server schedule
  // returns we may briefly leak the orphaned push (it'll still fire),
  // which is acceptable — the user has already moved on.
  const start = useCallback(
    async (seconds: number, label: string) => {
      if (seconds <= 0) return;
      // 0. Open the audio session. This runs synchronously inside the tap that
      // ticked the set off, which is the only moment iOS will allow it — an
      // AudioContext first touched from the expiry callback stays suspended
      // forever and the timer finishes in silence.
      unlockAudio();
      // 1. Synchronous local state update — happens before any await,
      // so the pill renders on the next paint regardless of network.
      const expiresAtMs = Date.now() + seconds * 1000;
      const previousID = active && active.phase === 'running' ? active.id : 0;
      clearTimers();
      setActive({
        id: 0,
        label,
        expiresAtMs,
        totalSec: seconds,
        phase: 'running',
      });
      const remainingMs = Math.max(0, expiresAtMs - Date.now());
      expireTimerRef.current = window.setTimeout(() => {
        expire(expiresAtMs);
      }, remainingMs);
      tickIntervalRef.current = window.setInterval(() => {
        setTick((t) => t + 1);
      }, 250);

      // 2. Server scheduling — gated to one in-flight at a time so
      // we don't fire two cancel/start round-trips concurrently. If a
      // tap arrives while another is in flight we skip the backup push
      // for that tap; the local timer still works fine.
      if (startBusyRef.current) return;
      startBusyRef.current = true;
      try {
        if (previousID > 0) {
          try {
            await api.cancelRest(previousID);
          } catch {
            // non-fatal — previous push will still fire if it lands
          }
        }
        try {
          const r = await api.startRest(seconds, label);
          // Only attach the new id if the active pill still matches
          // this invocation. Otherwise we'd overwrite a *newer* timer's
          // id with this stale one, breaking subsequent cancels.
          setActive((prev) =>
            prev && prev.expiresAtMs === expiresAtMs && prev.id === 0
              ? { ...prev, id: r.id }
              : prev,
          );
        } catch (err) {
          console.warn('[rest] backup push schedule failed', err);
        }
      } finally {
        startBusyRef.current = false;
      }
    },
    [active, clearTimers, expire],
  );

  // cancel discards the in-progress timer and tells the server not to fire
  // the push. Called from the "Skip rest" button on the pill. If the
  // backup push hasn't been scheduled yet (id=0), skip the server call —
  // there's nothing to cancel.
  const cancel = useCallback(async () => {
    if (!active) return;
    clearTimers();
    const id = active.id;
    setActive(null);
    if (id <= 0) return;
    try {
      await api.cancelRest(id);
    } catch {
      // The push will still fire if the cancel request didn't land — not
      // worth bubbling the error since the user already moved on.
    }
  }, [active, clearTimers]);

  // dismiss is the "expired" version of cancel — no need to call the
  // server (the push has already fired or been suppressed by ack-on-dispatch).
  // Just drops the pill from the UI.
  const dismiss = useCallback(() => {
    clearTimers();
    setActive(null);
  }, [clearTimers]);

  // Tear down on unmount.
  useEffect(() => clearTimers, [clearTimers]);

  return {
    active,
    secondsRemaining: active
      ? Math.max(0, Math.ceil((active.expiresAtMs - Date.now()) / 1000))
      : 0,
    start,
    cancel,
    dismiss,
  };
}

